import React, { useState } from 'react';
import { 
  Mail, FileText, Eye, Download, Plus, Edit, Trash2, 
  Search, Filter, CheckCircle, AlertTriangle, Copy, 
  RefreshCw, ArrowRight
} from 'lucide-react';
import EmailPreview from '../../components/EmailPreview';

const EmailTemplatesPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [showPreview, setShowPreview] = useState(false);
  const [previewType, setPreviewType] = useState<'verification' | 'order_confirmation' | 'password_reset' | 'notification' | 'supplier_verification' | 'document_notification'>('verification');
  const [previewData, setPreviewData] = useState<any>({});

  // Sample email templates
  const emailTemplates = [
    {
      id: '1',
      name: 'Account Verification',
      type: 'verification',
      description: 'Sent to new users to verify their email address',
      lastUpdated: '2024-06-28',
      status: 'active'
    },
    {
      id: '2',
      name: 'Order Confirmation',
      type: 'order_confirmation',
      description: 'Sent to buyers when an order is placed',
      lastUpdated: '2024-06-27',
      status: 'active'
    },
    {
      id: '3',
      name: 'Password Reset',
      type: 'password_reset',
      description: 'Sent when a user requests a password reset',
      lastUpdated: '2024-06-26',
      status: 'active'
    },
    {
      id: '4',
      name: 'General Notification',
      type: 'notification',
      description: 'Generic notification template for various updates',
      lastUpdated: '2024-06-25',
      status: 'active'
    },
    {
      id: '5',
      name: 'Supplier Verification',
      type: 'supplier_verification',
      description: 'Sent to suppliers when their account is verified',
      lastUpdated: '2024-06-24',
      status: 'active'
    },
    {
      id: '6',
      name: 'Document Notification',
      type: 'document_notification',
      description: 'Sent to buyers when a new document is shared',
      lastUpdated: '2024-06-23',
      status: 'active'
    },
    {
      id: '7',
      name: 'Order Shipped',
      type: 'notification',
      description: 'Sent when an order is shipped',
      lastUpdated: '2024-06-22',
      status: 'draft'
    },
    {
      id: '8',
      name: 'Order Delivered',
      type: 'notification',
      description: 'Sent when an order is delivered',
      lastUpdated: '2024-06-21',
      status: 'draft'
    }
  ];

  // Filter templates based on search and type
  const filteredTemplates = emailTemplates.filter(template => {
    const matchesSearch = template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         template.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = filterType === 'all' || template.type === filterType;
    
    return matchesSearch && matchesType;
  });

  // Get status badge
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <CheckCircle className="h-3 w-3 mr-1" />
          Active
        </span>;
      case 'draft':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          <AlertTriangle className="h-3 w-3 mr-1" />
          Draft
        </span>;
      default:
        return null;
    }
  };

  // Handle preview
  const handlePreview = (type: any, id: string) => {
    // Set preview data based on template type
    let previewData = {};
    
    switch (type) {
      case 'verification':
        previewData = {
          name: 'John Doe',
          verificationLink: 'https://madeinbd.com/verify-email?token=sample-token'
        };
        break;
      case 'order_confirmation':
        previewData = {
          name: 'John Doe',
          orderDetails: {
            orderNumber: 'ORD-123456',
            orderDate: '2024-06-28',
            totalAmount: 149.97,
            items: [
              { name: 'Premium Cotton T-Shirt', quantity: 2, price: 29.99 },
              { name: 'Cotton Polo Shirt', quantity: 3, price: 29.99 }
            ],
            shippingAddress: '123 Main St, New York, NY 10001, USA',
            paymentMethod: 'Credit Card'
          }
        };
        break;
      case 'password_reset':
        previewData = {
          name: 'John Doe',
          resetLink: 'https://madeinbd.com/reset-password?token=sample-token'
        };
        break;
      case 'notification':
        previewData = {
          name: 'John Doe',
          subject: 'Important Update from Made in BD',
          message: 'We have an important update regarding your account. Please review the details below.',
          actionLink: 'https://madeinbd.com/account',
          actionText: 'View Details'
        };
        break;
      case 'supplier_verification':
        previewData = {
          name: 'Bengal Textiles Ltd.',
          verificationLink: 'https://madeinbd.com/seller-central'
        };
        break;
      case 'document_notification':
        previewData = {
          name: 'John Doe',
          documentType: 'Commercial Invoice',
          orderNumber: 'ORD-123456',
          documentLink: 'https://madeinbd.com/my-documents'
        };
        break;
      default:
        previewData = {};
    }
    
    setPreviewType(type);
    setPreviewData(previewData);
    setShowPreview(true);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Email Templates</h1>
              <p className="text-gray-600">Manage and customize email templates</p>
            </div>
            <div className="flex items-center space-x-4">
              <button className="bg-orange-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-orange-700 transition-colors flex items-center space-x-2">
                <Plus className="h-4 w-4" />
                <span>Create Template</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search templates by name or description..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>
            </div>
            <div className="flex gap-4">
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 text-sm"
              >
                <option value="all">All Types</option>
                <option value="verification">Verification</option>
                <option value="order_confirmation">Order Confirmation</option>
                <option value="password_reset">Password Reset</option>
                <option value="notification">Notification</option>
                <option value="supplier_verification">Supplier Verification</option>
                <option value="document_notification">Document Notification</option>
              </select>
            </div>
          </div>
        </div>

        {/* Templates Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredTemplates.map((template) => (
            <div key={template.id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow">
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-orange-100 rounded-lg">
                      <Mail className="h-5 w-5 text-orange-600" />
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900">{template.name}</h3>
                      <div className="text-sm text-gray-500">{template.type}</div>
                    </div>
                  </div>
                  {getStatusBadge(template.status)}
                </div>
                
                <p className="text-sm text-gray-600 mb-4">{template.description}</p>
                
                <div className="text-xs text-gray-500 mb-4">
                  Last updated: {template.lastUpdated}
                </div>
                
                <div className="flex space-x-2">
                  <button
                    onClick={() => handlePreview(template.type, template.id)}
                    className="flex-1 bg-orange-600 text-white py-2 rounded-lg text-sm font-medium hover:bg-orange-700 transition-colors flex items-center justify-center space-x-1"
                  >
                    <Eye className="h-4 w-4" />
                    <span>Preview</span>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* No Templates Found */}
        {filteredTemplates.length === 0 && (
          <div className="bg-white rounded-lg shadow-sm p-8 text-center">
            <Mail className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No templates found</h3>
            <p className="text-gray-600 mb-4">
              {searchQuery || filterType !== 'all'
                ? 'Try adjusting your search or filters'
                : 'Email templates are managed in production environment'}
            </p>
            {(searchQuery || filterType !== 'all') && (
              <button
                onClick={() => {
                  setSearchQuery('');
                  setFilterType('all');
                }}
                className="bg-orange-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-orange-700 transition-colors"
              >
                Clear Filters
              </button>
            )}
          </div>
        )}

        {/* ZeptoMail Integration Guide */}
        <div className="mt-8 bg-orange-50 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">ZeptoMail Integration</h3>
          <p className="text-gray-700 mb-4">
            Made in BD uses ZeptoMail for production email delivery. Email templates are configured in the production environment:
          </p>
          <ol className="space-y-2 text-gray-700 mb-6">
            <li className="flex items-start">
              <span className="bg-orange-100 text-orange-800 rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5 mr-2">1</span>
              <span>Log in to your ZeptoMail dashboard at <a href="https://zeptomail.zoho.com" target="_blank" rel="noopener noreferrer" className="text-orange-600 hover:text-orange-700">zeptomail.zoho.com</a></span>
            </li>
            <li className="flex items-start">
              <span className="bg-orange-100 text-orange-800 rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5 mr-2">2</span>
              <span>Navigate to "Templates" in the sidebar</span>
            </li>
          </ol>
          <div className="flex justify-end">
            <a 
              href="https://www.zoho.com/zeptomail/help/api/introduction-to-api.html" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-orange-600 hover:text-orange-700 font-medium flex items-center"
            >
              <span>ZeptoMail API Documentation</span>
              <ArrowRight className="h-4 w-4 ml-1" />
            </a>
          </div>
        </div>
      </div>

      {/* Email Preview Modal */}
      <EmailPreview
        isOpen={showPreview}
        onClose={() => setShowPreview(false)}
        emailType={previewType}
        emailData={previewData}
      />
    </div>
  );
};

export default EmailTemplatesPage;